
import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from 'xlsx';

interface ImportCandidatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  onSuccess: () => void;
}

interface CandidateImportData {
  sn: number;
  role: string;
  country: string;
  city: string;
  candidate_name: string;
  skillset: string;
  years_experience: number;
  linkedin_url: string;
  past_projects: string;
}

export function ImportCandidatesDialog({
  open,
  onOpenChange,
  projectId,
  onSuccess
}: ImportCandidatesDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState<CandidateImportData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    // Check file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Please select an Excel (.xlsx, .xls) or CSV file");
      return;
    }

    setFile(selectedFile);

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Transform data to match our structure
      const transformedData: CandidateImportData[] = jsonData.map((row: any, index) => ({
        sn: row['SN'] || row['sn'] || index + 1,
        role: row['Role'] || row['role'] || '',
        country: row['Country'] || row['country'] || '',
        city: row['City'] || row['city'] || '',
        candidate_name: row['Candidate Name'] || row['candidate_name'] || row['name'] || '',
        skillset: row['Skillset'] || row['skillset'] || row['skills'] || '',
        years_experience: parseInt(row['Years Experience'] || row['years_experience'] || row['experience'] || '0'),
        linkedin_url: row['LinkedIn URL'] || row['linkedin_url'] || row['linkedin'] || '',
        past_projects: row['Past Projects'] || row['past_projects'] || row['projects'] || ''
      }));

      setPreviewData(transformedData.slice(0, 5)); // Show first 5 rows for preview
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error("Error reading file. Please check the format.");
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!file || !projectId) return;

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to import candidates");
        return;
      }

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const candidatesData = jsonData.map((row: any, index) => ({
        sn: row['SN'] || row['sn'] || index + 1,
        role: row['Role'] || row['role'] || '',
        country: row['Country'] || row['country'] || '',
        city: row['City'] || row['city'] || '',
        candidate_name: row['Candidate Name'] || row['candidate_name'] || row['name'] || '',
        skillset: row['Skillset'] || row['skillset'] || row['skills'] || '',
        years_experience: parseInt(row['Years Experience'] || row['years_experience'] || row['experience'] || '0'),
        linkedin_url: row['LinkedIn URL'] || row['linkedin_url'] || row['linkedin'] || '',
        past_projects: row['Past Projects'] || row['past_projects'] || row['projects'] || '',
        project_id: projectId,
        user_id: user.id
      }));

      // Validate required fields
      const invalidRows = candidatesData.filter(candidate => 
        !candidate.candidate_name || !candidate.role || !candidate.country || !candidate.city
      );

      if (invalidRows.length > 0) {
        toast.error(`${invalidRows.length} rows have missing required fields (Name, Role, Country, City)`);
        return;
      }

      // Delete existing records for this specific user AND project combination before importing new data
      const { error: deleteError } = await supabase
        .from('internal_candidates')
        .delete()
        .eq('user_id', user.id)
        .eq('project_id', projectId);

      if (deleteError) {
        console.error('Error deleting existing candidates for this project:', deleteError);
        toast.error("Failed to clear existing candidates for this project");
        return;
      }

      const { error } = await supabase
        .from('internal_candidates')
        .insert(candidatesData);

      if (error) throw error;

      toast.success(`Successfully imported ${candidatesData.length} candidates`);
      onSuccess();
      handleCancel();
    } catch (error) {
      console.error('Error importing candidates:', error);
      toast.error("Failed to import candidates");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            Import Candidate Data
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file" className="text-white text-sm">
              Select Excel or CSV File
            </Label>
            <div className="flex items-center gap-4">
              <Input
                ref={fileInputRef}
                id="file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="bg-white border-primary/20 text-black"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-black bg-primary"
              >
                <Upload className="w-4 h-4 mr-2" />
                Browse
              </Button>
            </div>
          </div>

          {file && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm">Selected: {file.name}</span>
              </div>

              {previewData.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white text-sm">Preview (first 5 rows):</Label>
                  <div className="bg-secondary/50 border border-primary/20 rounded p-3 max-h-48 overflow-auto">
                    <div className="text-[9px] text-primary-light grid grid-cols-9 gap-2 mb-2 font-semibold">
                      <span>SN</span>
                      <span>Role</span>
                      <span>Country</span>
                      <span>City</span>
                      <span>Name</span>
                      <span>Skillset</span>
                      <span>Experience</span>
                      <span>LinkedIn</span>
                      <span>Projects</span>
                    </div>
                    {previewData.map((candidate, index) => (
                      <div key={index} className="text-[9px] text-white grid grid-cols-9 gap-2 py-1">
                        <span>{candidate.sn}</span>
                        <span className="truncate">{candidate.role}</span>
                        <span className="truncate">{candidate.country}</span>
                        <span className="truncate">{candidate.city}</span>
                        <span className="truncate">{candidate.candidate_name}</span>
                        <span className="truncate">{candidate.skillset}</span>
                        <span>{candidate.years_experience}</span>
                        <span className="truncate">{candidate.linkedin_url}</span>
                        <span className="truncate">{candidate.past_projects}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
                <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-yellow-200">
                  <div className="font-medium mb-1">Required columns:</div>
                  <div>SN, Role, Country, City, Candidate Name</div>
                  <div className="mt-1">Optional columns: Skillset, Years Experience, LinkedIn URL, Past Projects</div>
                  <div className="mt-1 font-medium text-red-300">Warning: All existing candidate data for this project will be replaced with the imported data.</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end gap-2 pt-4">
          <Button 
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isUploading}
            className="px-8"
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleImport}
            disabled={!file || isUploading}
            className="bg-primary hover:bg-primary/90 text-black px-8"
          >
            {isUploading ? 'Importing...' : 'Import Candidates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
